import { Controller } from "@hotwired/stimulus"
import { get, post, destroy } from "@rails/request.js"
import tippy from 'tippy.js'

export default class extends Controller {
  static targets = ["calendar", "appointmentsJson"]
  static values = { 
    componentId: Number,
    batchId: Number,
    persistedColor: String,
    persistedText: String,
    modifiedColor: String,
    modifiedText: String
  }

  connect() {
    if (this.hasCalendarTarget) {
      this.initializeCalendar()
      this.element.addEventListener('component-lessons-scheduler:add-events', this.addEventsToCalendar.bind(this))
    }
    this.tooltipInstance = null
  }

  disconnect() {
    this.persistSaveLessons('0');
  }

  initializeCalendar() {
    const calendarHeight = this.calendarTarget.clientHeight || 450
    
    this.calendar = new FullCalendar.Calendar(this.calendarTarget, {
      initialView: 'timeGridWeek',
      customButtons: {
        datePicker: {
          text: '📅',
          click: () => this._dateInput.showPicker()
        }
      },
      headerToolbar: {
        left: 'prev,next today datePicker',
        center: 'title',
        right: 'dayGridMonth,timeGridWeek'
      },
      locale: 'pt-br',
      selectable: true,
      droppable: true,
      selectMirror: true,
      editable: true,
      select: this.handleDateSelect.bind(this),
      eventClick: this.handleEventClick.bind(this),
      eventDrop: this.handleEventDrop.bind(this),
      eventResize: this.handleEventResize.bind(this),
      eventMouseEnter: this.handleEventMouseEnter.bind(this),
      eventMouseLeave: this.handleEventMouseLeave.bind(this),
      eventDidMount: this.handleEventDidMount.bind(this),
      height: calendarHeight,
      scrollTime: '08:00:00',
      scrollTimeReset: false,
      buttonText: {
        today: 'Hoje',
        month: 'Mês',
        week: 'Semana',
        day: 'Dia'
      }
    })
    this.calendar.render()
    window.calendar = this.calendar

    this._dateInput = document.createElement('input')
    this._dateInput.type = 'date'
    Object.assign(this._dateInput.style, { position: 'absolute', opacity: '0', width: '1px', height: '1px', pointerEvents: 'none' })
    this._dateInput.addEventListener('change', (e) => {
      if (e.target.value) this.calendar.gotoDate(e.target.value)
    })
    this.element.appendChild(this._dateInput)
  }

  async handleDateSelect(selectInfo) {
    const startTime = this.convertToUTCString(selectInfo.start)
    const endTime = this.convertToUTCString(selectInfo.end)
    const date = selectInfo.start.toISOString().split('T')[0]
    const weekDay = selectInfo.start.getDay()

    const params = new URLSearchParams({
      start_time: startTime,
      end_time: endTime,
      date: date,
      week_day: weekDay,
      component_id: this.componentIdValue,
    })

    try {
      const newLessonScheduleUrl = `/admin/courses/batches/${this.batchIdValue}/lesson_schedules/new`
      await get(`${newLessonScheduleUrl}?${params.toString()}`, {
        responseKind: "turbo-stream" 
      })
    } catch (error) {
      console.error('Erro ao carregar modal de agendamento:', error)
    }

    this.calendar.unselect()
  }

  async handleEventClick(clickInfo) {
    const event = clickInfo.event
    const appointmentId = parseInt(event.id);

    if (isNaN(appointmentId) || appointmentId <= 0) {
      if (confirm('Este evento ainda não foi salvo. Deseja excluir?')) {
         this.handleEventMouseLeave();
         event.remove();
      }
      return;
    }

    try {
      await get(`/admin/courses/batches/${this.batchIdValue}/lesson_schedules/${appointmentId}`, {
        responseKind: "turbo-stream"
      })
    } catch (error) {
      console.error('Erro ao carregar detalhes do evento:', error)
    }
  }

  handleEventDrop(info) {
    this.markEventAsModified(info.event)
    this.handleEventMouseLeave()
    this.addEventClass(info.event.el, 'fc-event-modified')
  }

  handleEventResize(info) {
    this.markEventAsModified(info.event)
    this.handleEventMouseLeave()
    this.addEventClass(info.event.el, 'fc-event-modified')
  }

  handleEventDidMount(info) {
    if (info.event.extendedProps?.modified) {
      this.addEventClass(info.event.el, 'fc-event-modified')
    } else {
      this.addEventClass(info.event.el, 'fc-event-persisted')
    }

    if (info.event.extendedProps?.isNew) {
      this.addEventClass(info.event.el, 'fc-event-new')
      setTimeout(() => {
        this.removeEventClass(info.event.el, 'fc-event-new')
      }, 300)
    }
  }

  markEventAsModified(event) {
    event.setProp('backgroundColor', this.modifiedColorValue)
    event.setProp('borderColor', this.modifiedColorValue)
    event.setProp('textColor', this.modifiedTextValue)

    event.setExtendedProp('modified', true)
  }

  async saveLessonSchedule() {
    this.persistSaveLessons('1');
  }

  async persistSaveLessons(reloadSidebar) {
    try {
      this.calendar.getEvents().forEach(event => {
        if (event.extendedProps?.modified) {
          this.addEventClass(event.el, 'fc-event-saving')
        }
      })

      let rows = [];

      for (const event of this.calendar.getEvents()) {
        const new_event = this.parse(event);
        if (new_event?.readonly) {
          continue;
        }

        rows.push(new_event);
      }

      const data = {
        scheduler_data: {
          component_id: this.componentIdValue,
          reload_sidebar: reloadSidebar,
          events: rows
        },
      }

      await post(`/admin/courses/batches/${this.batchIdValue}/lesson_schedules`, {
        body: data,
        responseKind: "turbo-stream"
      })

      this.calendar.getEvents().forEach(event => {
        this.removeEventClass(event.el, 'fc-event-saving')
        this.removeEventClass(event.el, 'fc-event-modified')
        this.addEventClass(event.el, 'fc-event-persisted')
      })
    } catch (e) {
      console.error("Error saving", e);
      this.calendar.getEvents().forEach(event => {
        this.removeEventClass(event.el, 'fc-event-saving')
      })
    }
  }

  addEventsToCalendar(event) {
    const events = event.detail.events
    const reload = event.detail.reload || false
    
    if (reload) {
      const existingEvents = this.calendar.getEvents()
      existingEvents.forEach(existingEvent => {
        existingEvent.remove()
      })
    }

    events.forEach(eventData => {
      this.calendar.addEvent(eventData)
    })
  }


  parse(event) {
    try {
      return {
        id: event.id,
        uuid: event.extendedProps.uuid,
        type: event.extendedProps.type,
        title: event.title,
        start: this.convertToUTCString(event.start),
        end: this.convertToUTCString(event.end),
        color: event.backgroundColor,
        textColor: event.textColor,
        input: event.extendedProps.input,
        readonly: event.extendedProps.extras?.readonly,
        modified: event.extendedProps.modified || false,
        extras: event.extendedProps.extras,
      }
    } catch (e) {
      console.error("Error parsing event", e);
    }

    return null;
  }

  handleEventMouseEnter(info) {
    const event = info.event
    const tooltipContent = this.buildTooltipContent(event)
    
    this.tooltipInstance = tippy(info.el, {
      content: tooltipContent,
      allowHTML: true,
      animation: 'scale',
      theme: 'calendar-tooltip',
      arrow: true,
      placement: 'top',
      hideOnClick: false,
      trigger: 'manual',
      duration: [100, 0],
      delay: [300, 0]
    })
    
    this.tooltipInstance.show()
  }

  handleEventMouseLeave() {
    if (this.tooltipInstance) {
      this.tooltipInstance.destroy()
      this.tooltipInstance = null
    }
  }

  buildTooltipContent(event) {
    const startDate = new Date(event.start)
    const endDate = new Date(event.end)
    
    const formatDate = (date) => {
      return date.toLocaleDateString('pt-BR', { 
        weekday: 'long',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    }
    
    const formatTime = (date) => {
      return date.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const title = event.title || 'Evento'
    const lessonName = title.split('(')[0] || title
    const moduleName = title.split(' - ')[1] || title

    return `
      <div class="p-3 text-sm">
        <div class="font-semibold text-slate-900 mb-2 text-base">${lessonName}</div>
        <div class="text-blue-700 mb-1">
          <strong>Módulo:</strong> ${moduleName}
        </div>
        <div class="text-slate-700 mb-1">
          <strong>Data:</strong> ${formatDate(startDate)}
        </div>
        <div class="text-slate-700 mb-1">
          <strong>Horário:</strong> ${formatTime(startDate)} - ${formatTime(endDate)}
        </div>
        ${this.buildProfessorsDiv(event)}
      </div>
    `
  }

  buildProfessorsDiv(event) {
     try {
        if (event &&
           event.extendedProps &&
           event.extendedProps.extras &&
           event.extendedProps.extras.professors &&
           event.extendedProps.extras.professors.length > 0) {
           const names = event.extendedProps.extras.professors;
           const label = names.includes(" ") ? "Professores" : "Professor";

           return `
           <div class="text-slate-700 mb-1">
             <strong>${label}:</strong> ${names}
           </div>`;
       }
     } catch (e) {
        console.error("Error building professors div", e);
     }
     return "";
  }

  addEventClass(eventEl, className) {
    if (eventEl && !eventEl.classList.contains(className)) {
      eventEl.classList.add(className)
    }
  }

  removeEventClass(eventEl, className) {
    if (eventEl && eventEl.classList.contains(className)) {
      eventEl.classList.remove(className)
    }
  }

  convertToUTCString(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')
    
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
  }
}
